import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import {
  matchPath,
  Navigate,
  Outlet,
  Route,
  Routes,
  useHref,
  useLocation,
  useParams,
  useRoutes
} from "react-router-dom";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";
import Helmet from "react-helmet";
import { usePage } from "../hooks/CmsHooks";
import { AnimatePresence } from "motion/react";
import UIMessages from "../components/UIMessages";
import MessageBanner from "../components/MessageBanner";
import {
  accountPageUrl,
  adminPagesUrl,
  articleRedirectBaseUrl,
  b2bCheckoutUrl,
  checkoutUrl,
  claimsWholesaleFormUrl,
  faqBaseUrl,
  guidesBaseUrl,
  loginUrl,
  mapBaseUrl,
  orderBaseUrl,
  postPurchaseUrl,
  productTypeBaseUrl,
  resetPasswordUrl,
  shopUrl,
  storiesBaseUrl
} from "../config/urls";
import { ToolTipPortal } from "./ToolTip";
import {
  usePathDefaultLanguage,
  usePathLanguages,
  useSetting
} from "../hooks/SettingsHooks";
import { useSiteFeatures } from "../hooks/useSiteFeatures";
import { useCurrentUser, useUser } from "../functional-components/UserContext";
import { useI18n } from "../hooks/TranslationHooks";
import { OverlayContextProvider } from "../functional-components/OverlayContext";
import {
  NavigationContextProvider,
  useNavigationContext
} from "../functional-components/NavigationContext";
import { getCurrentSite, htmlLangFor } from "../utils/SiteUtils";
import { CountryRedirectHandler } from "./CountryRedirection/CountryRedirectHandler";
import { CountryRedirectContextProvider } from "./CountryRedirection/CountryRedirectContext";
import { useQuery } from "react-query";
import AutoCountrySiteRedirect from "./AutoCountrySiteRedirect";
import {
  ConsentManager,
  useConsentManager
} from "@pervanovo/bjelin-consent-manager";
import "@pervanovo/bjelin-consent-manager/bjelin-consent-manager.css";
import {
  useConsentManagerTranslations,
  useConsentVendorTranslations
} from "../hooks/ConsentHooks";
import { isLanguage } from "../config/languages";
import useTestcafe from "../hooks/useTestcafe";
import { compile, match } from "path-to-regexp";
import LoadingAnim from "./LoadingAnim";

const TopNavigation = lazy(() => import("../layout/TopNavigation"));
const OverlayCover = lazy(async () => ({
  default: (await import("./modal/GenericOverlay")).OverlayCover
}));

const ProductSupportPage = lazy(() => import("../pages/ProductSupportPage"));
const ResetPasswordPage = lazy(() => import("../pages/ResetPasswordPage"));
const LoginPage = lazy(() => import("../pages/LoginPage"));
const AccountPages = lazy(() => import("../pages/AccountPages"));
const ShopFrontPage = lazy(() => import("../pages/ShopFrontPage"));
const StoriesPage = lazy(() => import("../pages/StoriesPage"));
const StoryPage = lazy(() => import("../pages/StoryPage"));
const GuidePage = lazy(() => import("../pages/GuidePage"));
const GuideCategoryPage = lazy(() => import("../pages/GuideCategoryPage"));
const ProductGuidesPage = lazy(() => import("../pages/ProductGuidesPage"));
const GuidesPage = lazy(() => import("../pages/GuidesPage"));
const NewsListPage = lazy(() => import("../pages/NewsListPage"));
const NewsArticlePage = lazy(() => import("../pages/NewsArticlePage"));
const FaqPage = lazy(() => import("../pages/FaqPage"));
const FaqItemPage = lazy(() => import("../pages/FaqPage/FaqItemPage"));
const ClaimTypePage = lazy(() => import("../pages/ClaimTypePage"));
const ClaimTypeFormPage = lazy(() => import("../pages/ClaimTypePage/b2c_form"));
const ClaimWholesaleFormPage = lazy(() =>
  import("../pages/ClaimTypePage/b2b_form")
);
const MapPage = lazy(() => import("../pages/MapPage"));
const SearchPage = lazy(() => import("../pages/SearchPage"));
const ProductsPage = lazy(() => import("../pages/ProductsPage"));
const ProductPage = lazy(() => import("../pages/ProductPage"));
const ProductPrintPage = lazy(() => import("../pages/ProductPage/print"));
const ProductTypePrintPage = lazy(() =>
  import("../pages/ProductTypePage/print")
);
const ProductLabelPrintPage = lazy(() =>
  import("../pages/ProductLabelPrintPage")
);
const ProductPriceTagPrintPage = lazy(() =>
  import("../pages/ProductPriceTagPrintPage")
);
const B2BCheckoutPage = lazy(() => import("../pages/CheckoutPage/b2b"));
const CheckoutSuccessResurs = lazy(() =>
  import("../pages/CheckoutSuccessResurs")
);
const CheckoutFailResurs = lazy(() => import("../pages/CheckoutFailResurs"));
const PostPurchasePage = lazy(() => import("../pages/PostPurchasePage"));
const CmsPage = lazy(() => import("../pages/CmsPage"));
const OrderDetailsPage = lazy(() => import("../pages/OrderDetailsPage"));
const CmsPageLivePreview = lazy(() => import("../pages/CmsPageLivePreview"));
const AdminPages = lazy(() => import("../pages/AdminPages"));
const ProductPageLivePreview = lazy(() =>
  import("../pages/ProductPageLivePreview")
);
const StoryPageLivePreview = lazy(() =>
  import("../pages/StoryPageLivePreview")
);
const LegacyRedirects = lazy(() => import("./LegacyRedirects"));
const ProductSelectTestPage = lazy(() => import("./ProductSelect/testpage"));
const ClaimFormFieldTestPage = lazy(() =>
  import("../pages/ClaimTypePage/ClaimFormFieldTestPage")
);
const UIMessagesTestPage = lazy(() =>
  import("./UIMessages/UIMessagesTestPage")
);
const DownloadPage = lazy(() => import("../pages/DownloadPage"));
const CheckoutPageRoute = lazy(() => import("./routes/CheckoutPageRoute"));
const NotFoundPrintPage = lazy(() => import("./../pages/NotFoundPrintPage"));

const CmsPageRoute = props => {
  const params = useParams();
  const path =
    "/" + (params.path || "") + (params["*"] ? "/" + params["*"] : "");
  return (
    <CmsPage
      key={"cms-page-" + path} /* This forces a mount of a new page */
      {...props}
      path={path}
    />
  );
};

const StoriesPageRoute = () => {
  const page = usePage(storiesBaseUrl);
  return <StoriesPage page={page} />;
};

const StoryPageRoute = () => {
  return <StoryPage />;
};

const StoryPageOverlay = () => {
  return <StoryPage displayAsOverlay />;
};

const NewsArticlePageRoute = () => {
  const params = useParams();
  const newsId = params.newsPath.split("-")[0];
  return <NewsArticlePage newsId={newsId} />;
};

const ClaimTypePageRoute = () => {
  const params = useParams();
  const claimTypeId = params.claimType.split("-")[0];
  return <ClaimTypePage claimTypeId={claimTypeId} />;
};

const ClaimTypeFormPageRoute = () => {
  const params = useParams();
  const claimTypeId = params.claimType.split("-")[0];
  return <ClaimTypeFormPage claimTypeId={claimTypeId} />;
};

const ClaimWholesaleFormPageRoute = () => {
  return <ClaimWholesaleFormPage />;
};

const ProductsPageRoute = () => {
  const params = useParams();
  if ((params.category || "").match(/^story-.*/)) {
    return <LegacyRedirects />;
  }
  return <ProductsPage />;
};

const ProductPageRoute = () => {
  const params = useParams();
  const artId = (params.art_id || "").split("-")[0];
  //console.log("ProductPageRoute params", params, artId);
  return <ProductPage key={artId} artId={artId} />;
};
const ProductPageOverlay = () => {
  const params = useParams();
  const artId = (params.art_id || "").split("-")[0];
  //console.log("ProductPageOverlay params", params, artId);
  return <ProductPage key={artId} artId={artId} displayAsOverlay />;
};

const overlayRoutes = [
  {
    path: "/shop/:category/:art_id",
    element: <ProductPageOverlay />
  },
  {
    path: storiesBaseUrl + "/:story_slug",
    element: <StoryPageOverlay />
  },
  {
    path: articleRedirectBaseUrl + "/:art_id",
    element: <ProductPageOverlay />
  },
  {
    path: faqBaseUrl + "/:category/q/:question",
    element: <FaqItemPage displayAsOverlay />
  }
];

const DummyErrorRoute = () => {
  throw new Error("Dummy error");
};

const CheckoutSuccessResursRoute = () => {
  const { orderUuid } = useParams();
  return <CheckoutSuccessResurs orderUuid={orderUuid} />;
};

const CheckoutFailResursRoute = () => {
  const { orderUuid } = useParams();
  return <CheckoutFailResurs />;
};

//Returns an overlay route matching location, if any
export function matchOverlayRoute(location) {
  if (location && location.pathname) {
    if (overlayBlacklistPaths.indexOf(location.pathname) >= 0) {
      return undefined;
    }
    return overlayRoutes.find(
      route => matchPath(route.path, location.pathname) !== null
    );
  }
  return undefined;
}
const overlayBlacklistPaths = [postPurchaseUrl];

const LocalizedRouter = () => {
  const location = useLocation();
  /** This is a ref because we do not want re-routing when it changes */
  const navContext = useNavigationContext();
  const prevLocation = useRef(false);
  const { b2b } = useUser();
  let mainLocation = location;
  console.log("location:", location.pathname);
  const features = useSiteFeatures();
  const { showConsent } = useConsentManager();

  useEffect(() => {
    window.showConsent = showConsent;
  }, [showConsent]);

  const routes = [
    { path: "/test/product-select", element: <ProductSelectTestPage /> },
    { path: "/test/ui-messages", element: <UIMessagesTestPage /> },
    { path: "/test/claim-form-field", element: <ClaimFormFieldTestPage /> },
    { path: "/shop/product.php", element: <LegacyRedirects /> },
    { path: "/p/*", element: <LegacyRedirects /> },
    { path: "/shop/list_products.php", element: <LegacyRedirects /> },
    { path: "/api/pdf/:pdf_filename", element: <DownloadPage /> },
    { path: resetPasswordUrl, element: <ResetPasswordPage /> },
    { path: loginUrl, element: <LoginPage /> },
    { path: accountPageUrl + "/*?", element: <AccountPages /> },
    { path: adminPagesUrl + "/*?", element: <AdminPages /> },
    ...(b2b
      ? [
          {
            path: b2bCheckoutUrl,
            element: <B2BCheckoutPage />
          }
        ]
      : [
          {
            path: checkoutUrl,
            element: <CheckoutPageRoute />
          }
        ]),
    {
      path: postPurchaseUrl,
      element: <PostPurchasePage />
    },
    {
      path: "/shop/checkout/success/resurs/:orderUuid",
      element: <CheckoutSuccessResursRoute />
    },
    {
      path: "/shop/checkout/fail/resurs/:orderUuid",
      element: <CheckoutFailResursRoute />
    },
    {
      path: shopUrl,
      element: <ShopFrontPage />
    },
    {
      path: articleRedirectBaseUrl + "/:art_id",
      element: <ProductPageRoute />
    },
    {
      path: "/internal/print-label",
      element: <ProductLabelPrintPage />
    },
    {
      path: "/internal/price-tag",
      element: <ProductPriceTagPrintPage />
    },
    {
      path: "/shop/:category/:art_id",
      element: <ProductPageRoute />
    },
    {
      path: "/shop/:category",
      element: <ProductsPageRoute />
    },
    { path: "/pageLivePreview", element: <CmsPageLivePreview /> },
    {
      path: "/productLivePreview",
      element: <ProductPageLivePreview />
    },
    {
      path: "/storyPageLivePreview",
      element: <StoryPageLivePreview />
    },
    { path: storiesBaseUrl, element: <StoriesPageRoute /> },
    { path: storiesBaseUrl + "/type/:type", element: <StoriesPageRoute /> },
    { path: storiesBaseUrl + "/:story_slug", element: <StoryPageRoute /> },
    {
      path: productTypeBaseUrl + "/:product_type_path?/:art_slug?",
      element: <ProductSupportPage />
    },
    ...(features.guides()
      ? [
          { path: guidesBaseUrl, element: <GuidesPage /> },
          {
            path: guidesBaseUrl + "/p/:art_id",
            element: <ProductGuidesPage />
          },
          {
            path: guidesBaseUrl + "/c/:category_slug",
            element: <GuideCategoryPage />
          },
          {
            path: guidesBaseUrl + "/p/:art_id/:guide_slug/:step?",
            element: <GuidePage />
          },
          {
            path: guidesBaseUrl + "/:guide_slug/:step?",
            element: <GuidePage />
          }
        ]
      : []),

    {
      path: faqBaseUrl + "/:category/q/:question",
      element: <FaqItemPage />
    },
    {
      path: faqBaseUrl + "/:category?/:tag?",
      element: <FaqPage />
    },

    { path: mapBaseUrl, element: <MapPage /> },

    {
      path: "/claims/type/:claimType/form",
      element: <ClaimTypeFormPageRoute />
    },
    {
      path: "/claims/type/:claimType",
      element: <ClaimTypePageRoute />
    },
    {
      path: claimsWholesaleFormUrl(),
      element: <ClaimWholesaleFormPageRoute />
    },
    {
      path: "/search",
      element: <SearchPage />
    },
    {
      path: orderBaseUrl + "/:uuid",
      element: <OrderDetailsPage />
    },
    {
      path: "/dummy-error",
      element: <DummyErrorRoute />
    },
    {
      path: "/docs/*",
      element: <DownloadPage />
    },
    {
      path: "/:path?/*",
      element: <CmsPageRoute />
    }
  ];

  const disabledFeatureRedirects = [];

  if (!features.login()) {
    disabledFeatureRedirects.push({ from: loginUrl, to: "/", exact: true });
    disabledFeatureRedirects.push({ from: accountPageUrl, to: "/" });
  }
  if (!features.shop()) {
    //This might seem confusing, but /shop/floors is acting like a catalog in this case
    //redirects.push({ from: "/shop", to: "/shop/floors", exact: true });
    disabledFeatureRedirects.push({ from: checkoutUrl, to: "/" });
  }
  if (!features.products()) {
    disabledFeatureRedirects.push({ from: "/shop", to: "/" });
  }

  const firstOverlayMatch = matchOverlayRoute(location);
  console.log("firstOverlayMatch", firstOverlayMatch);
  const isOverlay = firstOverlayMatch !== undefined;
  //The route will be displayed as overlay - if there is a previous page to display it over
  const noOverlay = location?.state?.noOverlay === true;
  const displayAsOverlay =
    isOverlay &&
    // eslint-disable-next-line react-compiler/react-compiler
    prevLocation.current &&
    // eslint-disable-next-line react-compiler/react-compiler
    mainLocation.pathname !== prevLocation.current.pathname &&
    !noOverlay;
  if (displayAsOverlay) {
    // eslint-disable-next-line react-compiler/react-compiler
    mainLocation = prevLocation.current;
    console.log("display as overlay");
  } else {
    //Store it for use as overlay background later
    // eslint-disable-next-line react-compiler/react-compiler
    prevLocation.current = location;
    //console.log("set prevLocation:", location.pathname);
  }

  console.log("prevLocation:", prevLocation.current.pathname);
  console.log("mainLocation:", mainLocation.pathname);
  console.log("disabledFeatureRedirects:", disabledFeatureRedirects);

  const isPrint = location.pathname.includes("/print/");

  useEffect(() => {
    if (window.bjelinLoadingAnim) window.bjelinLoadingAnim.hide();
  }, []);

  return (
    <>
      <AnimatePresence initial={false}>
        <Suspense
          fallback={isPrint ? null : <LoadingAnim log={"LocalizedRouter"} />}
        >
          <Routes location={mainLocation} key={"mainRoutes"}>
            {disabledFeatureRedirects.flatMap(({ from, to, ...rest }) =>
              (Array.isArray(from) ? from : [from]).map(from => {
                console.log("redirects", from, to);
                return (
                  <Route
                    key={from}
                    path={from}
                    element={<Navigate replace to={to} />}
                    {...rest}
                  />
                );
              })
            )}
            {routes.map(({ path, ...rest }) => {
              return <Route key={path} path={path} {...rest} />;
            })}
            <Route path={"/news"}>
              <Route index={true} element={<NewsListPage />} />
              <Route path={":newsPath"} element={<NewsArticlePageRoute />} />
            </Route>
          </Routes>
        </Suspense>
      </AnimatePresence>
      <AnimatePresence>
        {displayAsOverlay && (
          <OverlayCover>
            <Suspense
              fallback={isPrint ? null : <LoadingAnim log={"overlay"} />}
            >
              <Routes location={location} key={"overlayRoutes"}>
                <Route
                  key={firstOverlayMatch.path}
                  path={firstOverlayMatch.path}
                  element={firstOverlayMatch.element}
                />
              </Routes>
            </Suspense>
          </OverlayCover>
        )}
      </AnimatePresence>
    </>
  );
};

function InitUserSecrets() {
  useCurrentUser(); //prepares the user context and sets the user secrets in userService
  return null;
}

function RemoveTrailingSlashes({ children }) {
  const { hash, search, pathname, state } = useLocation();
  if (pathname.length > 1 && pathname.match(new RegExp("/$"))) {
    console.log("Removing trailing slash!", pathname);
    return (
      <Navigate
        replace
        to={{
          pathname: pathname.replace(new RegExp("/+$"), ""),
          search,
          hash
        }}
        state={state}
      />
    );
  }
  return children;
}

const SetLocalizationAndContexts = ({ children }) => {
  const pathLang = useBasename();
  const location = useLocation();
  const site = getCurrentSite();
  const i18n = useI18n();
  const testcafe = useTestcafe();
  const { translations, purposeTranslations, specialPurposeTranslations } =
    useConsentManagerTranslations();
  const vendorTranslations = useConsentVendorTranslations();
  const lang = getCurrentSite() === "us" ? "en-us" : pathLang;
  const htmlLang = useMemo(() => htmlLangFor(site, pathLang), [site, pathLang]);
  const isInternal = location.pathname.match(/(^|\/)internal\//) !== null;

  const [langReady, setLangReady] = useState(i18n.language === lang);

  useEffect(() => {
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang).then(() => {
        console.log("LocalizedRoute lang changed", lang);
        setLangReady(true);
      });
    }
  }, [i18n, lang]);

  return langReady ? (
    <QueryParamProvider adapter={ReactRouter6Adapter}>
      <Helmet>
        <html lang={htmlLang} />
        <meta name={"site"} content={site} />
      </Helmet>
      <Suspense fallback={null}>
        <InitUserSecrets />
      </Suspense>
      <NavigationContextProvider lang={lang} pathLang={pathLang}>
        <CountryRedirectContextProvider>
          <OverlayContextProvider>
            <Suspense fallback={<LoadingAnim log={"router"} />}>
              <UIMessages />
              <AutoCountrySiteRedirect />
              <MessageBanner>
                <CountryRedirectHandler />
              </MessageBanner>
              {!isInternal && <TopNavigation />}
              <ToolTipPortal />
              <div className={"theme-white"}>
                <ConsentManager
                  translations={translations}
                  purposeTranslations={purposeTranslations}
                  specialPurposeTranslations={specialPurposeTranslations}
                  vendorTranslations={vendorTranslations}
                  autoShow={!testcafe}
                />
              </div>
              {children}
            </Suspense>
          </OverlayContextProvider>
        </CountryRedirectContextProvider>
      </NavigationContextProvider>
    </QueryParamProvider>
  ) : null;
};

function SetPrintSiteLang() {
  const i18n = useI18n();
  const params = useParams();
  const { site, lang: pathLang } = params;
  const lang = site === "us" ? "en-us" : pathLang;
  console.log("SetPrintSiteLang", params);

  useEffect(() => {
    if (window.bjelinLoadingAnim) window.bjelinLoadingAnim.hide();
  }, []);

  // suspend while changing i18n language
  useQuery(["printI18n", lang], () => i18n.changeLanguage(lang));

  return (
    <>
      <InitUserSecrets />
      <NavigationContextProvider lang={lang} pathLang={pathLang}>
        <Suspense fallback={null}>
          <Outlet />
        </Suspense>
      </NavigationContextProvider>
    </>
  );
}

export function PrintRoutes() {
  return useRoutes([
    {
      path: "/:site/:lang/*?",
      element: <SetPrintSiteLang />,
      children: [
        {
          path: "product/:art_id",
          element: <ProductPrintPage />
        },
        {
          path: "product-type/:product_type_path",
          element: <ProductTypePrintPage />
        },
        { path: "*?", element: <NotFoundPrintPage /> }
      ]
    },
    { path: "*?", element: <NotFoundPrintPage /> }
  ]);
}

const UnsupportedLangRedirect = ({ children }) => {
  const pathLang = useBasename();
  const location = useLocation();
  const params = useParams();
  const pathLanguages = usePathLanguages();
  const defaultLanguage = usePathDefaultLanguage();
  let path = params["*"];
  if (!isLanguage(pathLang) && pathLang.length !== 2) {
    path = pathLang + "/" + path;
  }
  const search = location.search || "";
  // console.log("UnsupportedLangRedirect", params, pathLanguages, pathLang);
  const pathLangIsValid = pathLanguages.indexOf(pathLang) >= 0;
  useEffect(() => {
    if (!pathLangIsValid) {
      const userLang = navigator.language
        ? navigator.language.substring(0, 2)
        : pathLang;
      const redirectLang = pathLanguages.includes(userLang)
        ? userLang
        : defaultLanguage;
      const to = `/${redirectLang}/r/${path}${search}`.replaceAll(
        new RegExp("//", "g"),
        "/"
      );
      console.warn("UnsupportedLangRedirect", to);
      // hard reload to be able to set basename in createBrowserRouter
      window.location.href = to;
    }
  }, [pathLangIsValid, defaultLanguage, path, pathLang, pathLanguages, search]);
  return pathLangIsValid ? children : null;
};

function DoubleLangRedirect({ children }) {
  const pathLang = useBasename();
  const location = useLocation();
  const pathHasDoubleLang =
    location.pathname.match(new RegExp(`^/${pathLang}(/|$)`)) !== null;
  // console.log("DoubleLangRedirect", location, pathHasDoubleLang);
  if (pathHasDoubleLang) {
    const pathname =
      location.pathname.replace(new RegExp(`^/${pathLang}`), "") || "/";
    console.log("DoubleLangRedirect", location.pathname, pathname);
    return (
      <Navigate
        to={{ pathname, search: location.search, hash: location.hash }}
        replace
      />
    );
  }
  return children;
}

const useBasename = () => {
  const href = useHref("/");
  return href.replace(/^\//, "");
};

function FixPrerenderRedirect({ children }) {
  const location = useLocation();
  if (location.pathname.match(new RegExp("^/r(/|$)")) !== null) {
    let pathname = location.pathname.replace(new RegExp("^/r"), "");
    if (pathname === "" || pathname === "/") {
      pathname = "//"; //ugly fix to add trailing slash to the landing page
    }
    console.log("FixPrerenderRedirect", location, pathname);
    return (
      <Navigate
        to={{
          pathname,
          search: location.search,
          hash: location.hash
        }}
        replace
      />
    );
  }
  return children;
}

function CockpitRedirects({ children }) {
  const redirects = useSetting("redirects", []);
  const location = useLocation();
  for (const redirect of redirects) {
    const end = !!redirect.exact;
    for (const from of redirect.from || []) {
      try {
        const matcher = match(from, { end });
        const pathnames = [location.pathname, decodeURI(location.pathname)];
        for (const pathname of pathnames) {
          const result = matcher(pathname);
          // console.log("CockpitRedirects", pathname, from, result, end, redirect.to);
          if (result) {
            const toPath = compile(redirect.to);
            const to = toPath(result.params);
            console.log(
              "CockpitRedirects match!",
              from,
              result,
              end,
              redirect.to,
              to
            );
            return <Navigate to={to} replace />;
          }
        }
      } catch (error) {
        console.warn(`Invalid redirect rule: ${from} => ${redirect.to}`);
      }
    }
  }
  return children;
}

const AddTrailingSlashToIndex = ({ children }) => {
  const { pathname, search, hash, state } = useLocation();
  if (pathname === "/") {
    const isMissingTrailingSlash =
      window.location.pathname.match(new RegExp("/", "g")).length < 2;
    console.log("AddTrailingSlashToIndex", {
      isMissingTrailingSlash,
      locationPathname: pathname,
      windowLocationPathname: window.location.pathname
    });
    if (isMissingTrailingSlash) {
      console.log("Adding trailing slash");
      return (
        <Navigate to={{ search, hash, pathname: "//" }} state={state} replace />
      );
    }
  }
  return children;
};

const MainRoutes = () => {
  return (
    <UnsupportedLangRedirect>
      <DoubleLangRedirect>
        <FixPrerenderRedirect>
          <AddTrailingSlashToIndex>
            <RemoveTrailingSlashes>
              <CockpitRedirects>
                <SetLocalizationAndContexts>
                  <LocalizedRouter />
                </SetLocalizationAndContexts>
              </CockpitRedirects>
            </RemoveTrailingSlashes>
          </AddTrailingSlashToIndex>
        </FixPrerenderRedirect>
      </DoubleLangRedirect>
    </UnsupportedLangRedirect>
  );
};

export default MainRoutes;
